import { ReactNode } from 'react';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTranslation } from 'react-i18next';
import InlineViewToggle from '../UI/InlineViewToggle';

/** A type section shows either its editable inputs or the impact calculated from them — never both. */
export type ScenarioSectionView = 'input' | 'impact';

interface ScenarioSectionProps {
    view: ScenarioSectionView;
    onViewChange: (next: ScenarioSectionView) => void;
    /** The two toggle labels ARE the section titles, which is why the grids no longer carry one. */
    inputTitle: string;
    impactTitle: string;
    /** The type's formula. Shown in BOTH views: it is what turns the inputs into the impact, so the
     *  calculated table has at least as much use for it as the editable one — and an icon that appears in
     *  one view and not the other moved everything below the heading by 2px on every toggle (CALL-784). */
    hints?: string[];
    /** Notes that only make sense over the editable table — which of its columns are calculated rather
     *  than entered. Appended to `hints` in the input view, so there is still ONE help icon. */
    inputHints?: string[];
    /** What qualifies the numbers on screen: the impact view's "preview of unsaved changes" / "last saved
     *  state" chip. It belongs to the HEADING rather than to the impact grid, which is where it started: a
     *  chip that exists in one view and not the other pushed the table (and everything below it) down by
     *  32px on every toggle of a dirty draft, on all five types (CALL-784). Here it sits inside a row both
     *  views render, whose minHeight already covers a small Chip — so it costs no height at all. */
    badge?: ReactNode;
    /** Header-right slot: a second axis the active view needs, e.g. Relativ/Absolut on the budget impact,
     *  or the "add row" button of the one type that has one. */
    actions?: ReactNode;
    children: ReactNode;
}

/** Enough for the tallest thing the heading row can hold — a small ToggleButtonGroup or an outlined
 *  Button — so a view that has neither does not make the page jump (CALL-784). */
const HEADING_MIN_HEIGHT = 34;

// One scenario type = one section, showing EITHER its inputs or its calculated impact (CALL-783). The two
// were stacked before, which made both unreadable past a handful of rows; the board implies a toggle and
// the Budget overview already had the pattern.
//
// A section deliberately renders only the active grid rather than hiding the other with CSS: the impact is
// a preview request while the draft is dirty, and mounting it invisibly would fire that request on every
// page load for every type.
//
// The notes sit in a tooltip rather than in the page flow: as paragraphs they ran three to five lines and
// pushed the table they describe off the first screen, and the people using this need them once. The icon is
// an IconButton so it is a tab stop — a bare <HelpOutlineIcon> inside a Tooltip is unreachable by keyboard.
const ScenarioSection = ({
    view,
    onViewChange,
    inputTitle,
    impactTitle,
    hints,
    inputHints,
    badge,
    actions,
    children,
}: ScenarioSectionProps) => {
    const { t } = useTranslation('budget');
    const notes = [...(hints ?? []), ...(view === 'input' ? (inputHints ?? []) : [])];

    return (
        <Box>
            <Stack
                // Addressable, so a test can assert what is IN this row rather than merely on the page —
                // notably the impact badge, whose whole point is that it costs no height because it lives
                // in the one row both views draw (CALL-784).
                data-testid="scenario-section-heading"
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 1, minHeight: `${HEADING_MIN_HEIGHT}px` }}
            >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <InlineViewToggle<ScenarioSectionView>
                        options={[
                            { value: 'input', label: inputTitle },
                            { value: 'impact', label: impactTitle },
                        ]}
                        value={view}
                        onChange={onViewChange}
                    />
                    {notes.length > 0 && (
                        <Tooltip
                            // Up and to the RIGHT, never below. Placed below, a 460 × 157 tooltip blanketed
                            // the header row and both data rows of the five columns its own text names,
                            // while ~1000px of the page sat unused beside it (CALL-784). Plain `right`
                            // centres the panel on the icon, which still reached ~78px down into the table;
                            // `right-end` aligns its BOTTOM with the icon, so it opens into the free space
                            // beside the metadata form and stops above the grid. The popper still flips on
                            // its own if there is no room above.
                            placement="right-end"
                            title={notes.join('\n\n')}
                            slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 460 } } }}
                        >
                            <IconButton size="small" aria-label={t('action.showCalculationNotes')}>
                                <HelpOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {badge}
                </Stack>
                {actions}
            </Stack>
            {children}
        </Box>
    );
};

export default ScenarioSection;
